import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { captureLocation } from '../utils/locationTracker';
import { compressImage, validateImageFile, getBase64SizeKB } from '../utils/photoUtils';
import { notifyServiceSubmitted, notifyEmergencyService } from '../utils/NotificationService';
import './RecordService.css';

const PHOTO_TYPES = [
  { key: 'before', label: 'Before',  icon: '🔴', description: 'Equipment on arrival' },
  { key: 'after',  label: 'After',   icon: '✅', description: 'After fix/service'   },
  { key: 'parts',  label: 'Parts',   icon: '🔧', description: 'Spare parts used'    },
  { key: 'serial', label: 'Serial',  icon: '🔢', description: 'Serial number tag'   }
];

function RecordService({ user, onBack }) {
  const [formData, setFormData] = useState({
    equipmentType: 'keg_pump',
    serialNumber: '',
    outletName: '',
    serviceType: 'routine',
    drinkTypes: [],
    faultDescription: '',
    partsUsed: '',
    notes: '',
    itemsAvailable: { recordBook: false, cleaningKit: false, diokem: false }
  });

  const [photos, setPhotos] = useState({ before: null, after: null, parts: null, serial: null });
  const [photoLoading, setPhotoLoading] = useState({});
  const [photoErrors, setPhotoErrors] = useState({});
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const beforeRef = useRef(null);
  const afterRef  = useRef(null);
  const partsRef  = useRef(null);
  const serialRef = useRef(null);
  const fileRefs  = { before: beforeRef, after: afterRef, parts: partsRef, serial: serialRef };

  const [gpsLocation, setGpsLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [outlets, setOutlets] = useState([]);

  useEffect(() => {
    const captureGPS = async () => {
      const location = await captureLocation(user, 'RecordService', { action: 'page_opened' });
      if (location) {
        setGpsLocation({ latitude: location.latitude, longitude: location.longitude, accuracy: location.accuracy });
        setSuccess('Location captured automatically!');
        setTimeout(() => setSuccess(''), 3000);
      }
    };
    captureGPS();
  }, [user]);

  useEffect(() => {
    const loadOutlets = async () => {
      try {
        const snap = await getDocs(collection(db, 'outlets'));
        setOutlets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading outlets:', err);
      }
    };
    loadOutlets();
  }, []);

  const handlePhotoCapture = async (photoKey, file) => {
    if (!file) return;
    const { valid, error: validError } = validateImageFile(file);
    if (!valid) { setPhotoErrors(prev => ({ ...prev, [photoKey]: validError })); return; }
    setPhotoLoading(prev => ({ ...prev, [photoKey]: true }));
    setPhotoErrors(prev => ({ ...prev, [photoKey]: '' }));
    try {
      const compressed = await compressImage(file, 800, 0.7);
      const sizeKB = getBase64SizeKB(compressed);
      const final = sizeKB > 250 ? await compressImage(file, 600, 0.5) : compressed;
      setPhotos(prev => ({ ...prev, [photoKey]: final }));
    } catch (err) {
      setPhotoErrors(prev => ({ ...prev, [photoKey]: 'Failed to process image. Please try again.' }));
    } finally {
      setPhotoLoading(prev => ({ ...prev, [photoKey]: false }));
    }
  };

  const handleRemovePhoto = (photoKey) => {
    setPhotos(prev => ({ ...prev, [photoKey]: null }));
    setPhotoErrors(prev => ({ ...prev, [photoKey]: '' }));
    if (fileRefs[photoKey]?.current) fileRefs[photoKey].current.value = '';
  };

  const triggerCamera = (photoKey) => {
    const ref = fileRefs[photoKey]?.current;
    if (!ref) return;
    ref.setAttribute('capture', 'environment');
    ref.click();
  };

  const triggerGallery = (photoKey) => {
    const ref = fileRefs[photoKey]?.current;
    if (!ref) return;
    ref.removeAttribute('capture');
    ref.click();
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    setError('');
    if (!navigator.geolocation) { setError('GPS not supported'); setGettingLocation(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGettingLocation(false);
        setSuccess('Location captured!');
        setTimeout(() => setSuccess(''), 3000);
      },
      (err) => {
        const msgs = { 1: 'GPS permission denied.', 2: 'Location unavailable.', 3: 'Location timed out.' };
        setError(msgs[err.code] || 'Unknown GPS error');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, itemsAvailable: { ...prev.itemsAvailable, [name]: checked } }));
  };

  const handleDrinkTypeToggle = (drinkType) => {
    setFormData(prev => {
      const drinkTypes = prev.drinkTypes.includes(drinkType)
        ? prev.drinkTypes.filter(d => d !== drinkType)
        : [...prev.drinkTypes, drinkType];
      return { ...prev, drinkTypes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serialNumber) { setError('Please enter equipment serial number'); return; }
    if (!formData.outletName)   { setError('Please enter outlet name'); return; }
    if (!gpsLocation)           { setError('Please capture GPS location first'); return; }
    setLoading(true);
    setError('');
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const servicesRef = collection(db, 'serviceRecords');
      const q = query(servicesRef, where('createdAt', '>=', today));
      const snapshot = await getDocs(q);
      const orderNumber = String(snapshot.size + 1).padStart(3, '0');
      await captureLocation(user, 'RecordService', { action: 'service_submitted', orderNumber, outletName: formData.outletName });
      const capturedPhotos = Object.fromEntries(Object.entries(photos).filter(([, v]) => v !== null));

      // ✅ NEW: look up the outlet's Territory/Division/Area chain so
      // reports can filter by it later. outletName is a free-typed field
      // (not a hard select), so this only finds a match for outlets that
      // actually exist in the outlets collection under that exact name —
      // if it doesn't match anything (a typo, or an outlet registered
      // before the org-structure change), the service still saves fine,
      // it just won't carry area info. Never blocks submission either way.
      const matchedOutlet = outlets.find(o => o.outletName === formData.outletName);

      const serviceData = {
        orderNumber,
        equipmentType: formData.equipmentType,
        serialNumber: formData.serialNumber,
        outletName: formData.outletName,
        outletId: matchedOutlet?.id || null,
        territoryId: matchedOutlet?.territoryId || null,
        territoryName: matchedOutlet?.territoryName || null,
        divisionId: matchedOutlet?.divisionId || null,
        divisionName: matchedOutlet?.divisionName || null,
        areaId: matchedOutlet?.areaId || null,
        areaName: matchedOutlet?.areaName || null,
        serviceType: formData.serviceType,
        drinkTypes: formData.drinkTypes,
        faultDescription: formData.faultDescription,
        partsUsed: formData.partsUsed,
        notes: formData.notes,
        itemsAvailable: formData.itemsAvailable,
        technicianId: user.id,
        technicianName: user.name,
        location: gpsLocation,
        photos: capturedPhotos,
        photoFlags: {},
        photoCount: Object.keys(capturedPhotos).length,
        status: 'completed',
        createdAt: serverTimestamp(),
        completedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'serviceRecords'), serviceData);

      // ✅ Trigger notifications for supervisors/admins
      if (formData.serviceType === 'emergency') {
        await notifyEmergencyService({ ...serviceData, orderNumber }, user.name);
      } else {
        await notifyServiceSubmitted({ ...serviceData, orderNumber }, user.name);
      }
      const photoMsg = Object.keys(capturedPhotos).length > 0 ? ` with ${Object.keys(capturedPhotos).length} photo(s)` : '';
      setSuccess(`Service record #${orderNumber} created successfully${photoMsg}!`);
      setFormData({ equipmentType: 'keg_pump', serialNumber: '', outletName: '', serviceType: 'routine', drinkTypes: [], faultDescription: '', partsUsed: '', notes: '', itemsAvailable: { recordBook: false, cleaningKit: false, diokem: false } });
      setPhotos({ before: null, after: null, parts: null, serial: null });
      setGpsLocation(null);
      setTimeout(() => { if (onBack) onBack(); }, 3000);
    } catch (err) {
      console.error('Error creating service record:', err);
      setError('Failed to create service record. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const drinkTypeOptions = ['Dark Extra', 'Regular', 'Others'];
  const capturedCount = Object.values(photos).filter(Boolean).length;

  if (lightboxPhoto) {
    const pt = PHOTO_TYPES.find(p => p.key === lightboxPhoto.key);
    return (
      <div className="lightbox-overlay" onClick={() => setLightboxPhoto(null)}>
        <div className="lightbox-content" onClick={e => e.stopPropagation()}>
          <div className="lightbox-header">
            <span className="lightbox-title">{pt?.icon} {pt?.label} Photo</span>
            <button className="lightbox-close" onClick={() => setLightboxPhoto(null)}>✕ Close</button>
          </div>
          <img src={lightboxPhoto.base64} alt={pt?.label} className="lightbox-img" />
        </div>
      </div>
    );
  }

  return (
    <div className="record-service">
      <div className="page-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>Record Service</h1>
      </div>
      <div className="service-form-container">
        <form onSubmit={handleSubmit} className="service-form">

          <div className="form-section">
            <h3>Equipment Type</h3>
            <div className="tab-buttons">
              <button type="button" className={`tab-button ${formData.equipmentType === 'keg_pump' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, equipmentType: 'keg_pump' }))}>☕ Keg Pump</button>
              <button type="button" className={`tab-button ${formData.equipmentType === 'draft_machine' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, equipmentType: 'draft_machine' }))}>🍺 Draft Machine</button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Serial Number / Equipment ID *</label>
            <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange}
              placeholder="e.g., KEG-12345" className="form-input" required />
          </div>

          <div className="form-group">
            <label className="form-label">Outlet Name *</label>
            <input type="text" name="outletName" value={formData.outletName} onChange={handleInputChange}
              placeholder="e.g., Kona Mbaya Bar" className="form-input" list="outlets-list" required />
            <datalist id="outlets-list">
              {outlets.map(outlet => (<option key={outlet.id} value={outlet.outletName} />))}
            </datalist>
          </div>

          <div className="form-group">
            <label className="form-label">Service Type</label>
            <div className="radio-group">
              {['routine', 'emergency', 'other'].map(type => (
                <label className="radio-label" key={type}>
                  <input type="radio" name="serviceType" value={type} checked={formData.serviceType === type} onChange={handleInputChange} />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Select Drink Types</label>
            <div className="chip-group">
              {drinkTypeOptions.map(drink => (
                <button key={drink} type="button" className={`chip ${formData.drinkTypes.includes(drink) ? 'active' : ''}`}
                  onClick={() => handleDrinkTypeToggle(drink)}>{drink}</button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Fault Description</label>
            <textarea name="faultDescription" value={formData.faultDescription} onChange={handleInputChange}
              placeholder="Describe the issue or work performed..." className="form-textarea" rows="3" />
          </div>

          <div className="form-group">
            <label className="form-label">Tick if Items are Available</label>
            <div className="checkbox-group">
              {[{ name: 'recordBook', label: 'Record Book' }, { name: 'cleaningKit', label: 'Cleaning Kit' }, { name: 'diokem', label: 'Diokem' }].map(item => (
                <label className="checkbox-label" key={item.name}>
                  <input type="checkbox" name={item.name} checked={formData.itemsAvailable[item.name]} onChange={handleCheckboxChange} />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Parts/Spares Used</label>
            <input type="text" name="partsUsed" value={formData.partsUsed} onChange={handleInputChange}
              placeholder="e.g., Pressure valve (1), Cleaning kit (1)" className="form-input" />
          </div>

          <div className="form-group">
            <label className="form-label">Additional Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange}
              placeholder="Any additional observations or comments..." className="form-textarea" rows="2" />
          </div>

          {/* PHOTO DOCUMENTATION */}
          <div className="form-section">
            <h3>
              📸 Photo Documentation
              {capturedCount > 0 && (
                <span className="photo-count-badge">{capturedCount} / {PHOTO_TYPES.length}</span>
              )}
            </h3>
            <p className="photo-section-note">
              Capture before/after photos to verify work and prevent disputes. Strongly recommended.
            </p>
            <div className="photo-grid">
              {PHOTO_TYPES.map(pt => {
                const captured = photos[pt.key];
                const isLoading = photoLoading[pt.key];
                const photoError = photoErrors[pt.key];
                return (
                  <div key={pt.key} className={`photo-card-light ${captured ? 'captured' : ''}`}>
                    <input type="file" accept="image/*" ref={fileRefs[pt.key]} style={{ display: 'none' }}
                      onChange={e => handlePhotoCapture(pt.key, e.target.files[0])} />
                    {captured ? (
                      <>
                        <div className="photo-thumb-wrapper" onClick={() => setLightboxPhoto({ key: pt.key, base64: captured })}>
                          <img src={captured} alt={pt.label} className="photo-thumb-img" />
                          <div className="photo-thumb-overlay">🔍 View</div>
                        </div>
                        <div className="photo-captured-label">
                          <span>{pt.icon} {pt.label}</span>
                          <span className="photo-size-kb">{getBase64SizeKB(captured)}KB</span>
                        </div>
                        <div className="photo-captured-actions">
                          <button type="button" className="photo-retake-btn" onClick={() => triggerCamera(pt.key)}>🔄 Retake</button>
                          <button type="button" className="photo-remove-btn" onClick={() => handleRemovePhoto(pt.key)}>🗑️</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="photo-empty-icon">{pt.icon}</div>
                        <div className="photo-type-label">{pt.label}</div>
                        <div className="photo-empty-desc">{pt.description}</div>
                        {isLoading ? (
                          <div className="photo-loading-text">⏳ Processing...</div>
                        ) : (
                          <div className="photo-capture-buttons">
                            <button type="button" className="photo-camera-btn" onClick={() => triggerCamera(pt.key)}>📷 Camera</button>
                            <button type="button" className="photo-gallery-btn" onClick={() => triggerGallery(pt.key)}>🖼️</button>
                          </div>
                        )}
                        {photoError && <p className="photo-error-text">⚠️ {photoError}</p>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {capturedCount > 0 && (
              <div className="photo-summary-bar">✅ {capturedCount} photo{capturedCount > 1 ? 's' : ''} ready to submit</div>
            )}
          </div>

          {/* GPS */}
          <div className="form-section">
            <h3>GPS Coordinates</h3>
            {gpsLocation ? (
              <div className="location-display">
                <div className="location-icon">📍</div>
                <div className="location-info">
                  <div className="location-coords">{gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)}</div>
                  <div className="location-accuracy">Accuracy: ±{gpsLocation.accuracy.toFixed(0)}m</div>
                </div>
                <button type="button" onClick={getCurrentLocation} className="btn-refresh" disabled={gettingLocation}>🔄</button>
              </div>
            ) : (
              <button type="button" onClick={getCurrentLocation} className="btn-get-location" disabled={gettingLocation}>
                {gettingLocation ? <><span className="spinner-small"></span>Getting Location...</> : <>📍 Capture GPS Location</>}
              </button>
            )}
          </div>

          {error   && <div className="alert alert-error">⚠️ {error}</div>}
          {success && <div className="alert alert-success">✓ {success}</div>}

          <button type="submit" className="btn btn-primary btn-submit" disabled={loading || !gpsLocation}>
            {loading
              ? <><span className="spinner-small"></span>Submitting...</>
              : `SUBMIT SERVICE RECORD${capturedCount > 0 ? ` + ${capturedCount} PHOTO${capturedCount > 1 ? 'S' : ''}` : ''}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RecordService;
